import { uploadData } from "aws-amplify/storage";

import { ApiClient } from "@/api/ApiClient";
import { getTenantId } from "@utils/Auth";
import { getHtmlFromRoute, sendPdfToGenerateApi } from "@utils/htmlExport";
import { renderRouteToExcel } from "@utils/renderRouteToExcel";
import { REPORT_EXCEL_CONTENT_TYPE } from "@utils/ReportExcel";
import {
    AccountingPackageMode,
    buildAccountingPackagePdfKey,
    buildAccountingPackageReports,
    buildAccountingPackageXlsxKey,
} from "./reports";

/**
 * Orquesta la generacion del paquete contable desde el navegador.
 *
 * Los PDF y los Excel se producen aqui porque solo la app cargada sabe renderizar
 * los reportes (mismo store, misma sesion, misma plantilla del tenant). Los
 * archivos se dejan en S3 y una lambda los comprime en un ZIP y lo envia por
 * correo: el generador de PDF externo escribe el archivo en su bucket y nunca
 * devuelve los bytes, asi que el navegador no puede armar el ZIP por su cuenta.
 */

/** Los reportes anuales cargan hasta 10.000 documentos; 20s del portal no alcanzan. */
const PACKAGE_RENDER_TIMEOUT_MS = 120000;

export type AccountingPackageProgress = {
    step: string,
    /** Pasos completados antes de iniciar el actual. */
    completed: number,
    total: number
}

export type GenerateAccountingPackageParams = {
    mode: AccountingPackageMode,
    year: string,
    month?: string,
    emails: string,
    onProgress?: (progress: AccountingPackageProgress) => void
}

export type AccountingPackageResultData = {
    zipName: string,
    attached: boolean,
    downloadUrl?: string
}

export const generateAccountingPackage = async (
    params: GenerateAccountingPackageParams
): Promise<AccountingPackageResultData> => {
    const { mode, year, month, emails, onProgress } = params;

    const tenantId = await getTenantId();
    const stamp = `${Date.now()}`;
    const reports = buildAccountingPackageReports(mode, year, month);

    // Dos archivos por reporte mas el envio del correo.
    const total = (reports.length * 2) + 1;
    let completed = 0;
    const notify = (step: string) => onProgress?.({ step, completed, total });

    for (const report of reports) {
        notify(`Generando PDF de ${report.label}`);
        const html = await getHtmlFromRoute(report.printPath, { timeoutMs: PACKAGE_RENDER_TIMEOUT_MS });
        const pdfResponse = await sendPdfToGenerateApi(
            html,
            buildAccountingPackagePdfKey(tenantId, stamp, report.key)
        );
        if (!pdfResponse.ok) {
            throw new Error(`No fue posible generar el PDF de ${report.label}`);
        }
        completed++;

        notify(`Generando Excel de ${report.label}`);
        const { bytes } = await renderRouteToExcel(report.excelPath, { timeoutMs: PACKAGE_RENDER_TIMEOUT_MS });
        await uploadData({
            path: buildAccountingPackageXlsxKey(tenantId, stamp, report.key),
            data: new Blob([bytes], { type: REPORT_EXCEL_CONTENT_TYPE }),
            options: { contentType: REPORT_EXCEL_CONTENT_TYPE },
        }).result;
        completed++;
    }

    notify("Enviando el paquete por correo");
    const { data, errors } = await ApiClient.mutations.createAccountingPackageMutation({
        toEmails: `${emails}`.split(",").map((email) => email.trim()).filter(Boolean),
        mode,
        year: `${year}`,
        month: mode === "month" ? `${month}` : undefined,
        stamp,
    });
    if (errors) throw errors[0];
    if (!data?.ok) throw new Error(data?.error || "Fallo el envio del paquete contable");
    completed++;

    return {
        zipName: `${data.zipName}`,
        attached: data.attached || false,
        downloadUrl: data.downloadUrl || undefined,
    };
}
